import { ActionReference, GameObjectReference, GameState } from "@shared/types";
import { customElement, state } from "lit/decorators.js";
import { LitElement, TemplateResult, css, html, nothing } from "lit";
import { getState, performAction } from "../services/routeService";
import { removePlayerSessionId } from "../helpers";

@customElement("game-canvas")
export class GameCanvas extends LitElement {
    @state() private backgroundMusicVolume: number = 0;
    @state() private effectsVolume: number = 0;

    public static styles = css`
        .game {
            height: 100%;
            display: grid;
            grid-template-columns: 1fr;
            grid-template-rows: auto auto 1fr auto;
            grid-column-gap: 0px;
            grid-row-gap: 0px;
        }

        .title {
            text-align: center;
            margin-top: 10px;
        }

        .volume-controls {
            position: absolute;
            top: 0;
            right: 0;
            padding: 10px;
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            background-color: rgba(255, 255, 255, 0.9);
            border-radius: 0 0 0 10px;
            z-index: 10;
            color: black;
        }

        .volume-controls label {
            font-size: 0.8rem;
        }

        .volume-controls input {
            background-color: #306230;
            -webkit-appearance: none;
            margin: 6px 0;
            width: 100%;
        }

        .volume-controls input[type="range"]::-webkit-slider-thumb {
            -webkit-appearance: none;
            appearance: none;
            width: 20px;
            height: 20px;
            background: #0f380f;
            cursor: pointer;
        }

        .volume-controls input[type="range"]::-moz-range-thumb {
            width: 20px;
            height: 20px;
            background: #0f380f;
            cursor: pointer;
        }

        .header {
            display: flex;
            flex-direction: column;
            align-items: center;
            flex-grow: 1;
            position: relative;
            margin-top: 10px;
        }

        .header img {
            width: 1000px;
            height: 400px;
            object-fit: cover;
            object-position: center;
            image-rendering: pixelated;
        }

        .header img:nth-child(n + 2) {
            position: absolute;
        }

        .content {
            flex-grow: 1;
            overflow: auto;
            margin-top: 10px;
            padding: 0 10px;
        }

        .content p {
            margin: 0 0 10px 0;
        }

        .content p:last-of-type {
            margin: 0;
        }

        .footer {
            border-radius: 10px 10px 0 0;
            background-color: #0f380f;
            border: 1px solid #306230;
            margin-top: 10px;
            display: flex;
            height: 105px;
        }

        .footer .buttons {
            display: flex;
            flex-direction: column;
            overflow: auto;
            padding: 10px 10px 0 10px;
        }

        .footer .button {
            background-color: #306230;
            border: 1px solid #0f380f;
            padding: 5px 10px;
            margin: 0 0 10px 10px;
            text-transform: uppercase;
            cursor: pointer;
            display: inline-block;
            user-select: none;
        }

        .footer .button.active,
        .footer .button:hover {
            background-color: #8bac0f;
        }
    `;

    private showPopup: boolean = true;

    private roomTitle?: string;
    private roomImages?: string[];
    private contentText?: string[];
    private actionButtons?: ActionReference[];
    private gameObjectButtons?: GameObjectReference[];
    private roomMusic?: string;

    private selectedActionButton?: ActionReference;
    private selectedGameObjectButtons: Set<GameObjectReference> = new Set<GameObjectReference>();

    public connectedCallback(): void {
        super.connectedCallback();

        void this.refreshState();
    }

    private async refreshState(): Promise<void> {
        const state: GameState = await getState();

        this.updateState(state);
    }

    private updateState(state: GameState): void {
        // Reset the component
        this.roomTitle = state.roomTitle;
        this.roomImages = state.roomImages;
        this.contentText = state.text;
        this.actionButtons = state.actions;

        this.gameObjectButtons = state.objects;
        this.roomMusic = state.music;

        console.log(this.roomTitle);
        console.log(this.roomMusic);

        this.selectedActionButton = undefined;
        this.selectedGameObjectButtons.clear();

        this.requestUpdate();
        this.handleMusicPlay();
    }

    private playSoundEffect(soundFileName: string): void {
        const audio: any = new Audio(`/assets/audio/effects/${soundFileName}`);
        audio.volume = this.effectsVolume;
        console.log("Play");
        audio.play().catch((error: any) => {
            console.error("Error playing sound effect:", error);
        });
    }
    

    private async handleClickAction(button: ActionReference): Promise<void> {
        this.playSoundEffect("effect.mp3");
        if (button.needsObject) {
            this.selectedActionButton = button;
            this.selectedGameObjectButtons.clear();

            this.requestUpdate();
        } else {
            const state: any = await performAction(button.alias);

            if (state === undefined) {
                return;
            }

            this.updateState(state);
        }
    }

    private handlePopupClick(): void {
        this.showPopup = false;

        this.handleMusicPlay();
        this.requestUpdate();
    }

    private handleMusicPlay(): void {
        const musicPlayer: HTMLAudioElement | null = this.shadowRoot?.getElementById(
            "roomMusic"
        ) as HTMLAudioElement;
        if (musicPlayer && this.roomMusic) {
            musicPlayer.src = `/assets/audio/rooms/${this.roomMusic}`;
            musicPlayer.volume = this.backgroundMusicVolume;
            musicPlayer.play().catch((error) => console.error("Error playing music:", error));
        }
    }

    private async handleClickObject(button: GameObjectReference): Promise<void> {
        if (!this.selectedActionButton) {
            return;
        }

        this.selectedGameObjectButtons.add(button);

        const state: GameState | undefined = await performAction(
            this.selectedActionButton.alias,
            [...this.selectedGameObjectButtons].map((e) => e.alias)
        );

        if (this.selectedGameObjectButtons.size >= 2) {
            this.selectedActionButton = undefined;
            this.selectedGameObjectButtons.clear();
        }

        this.requestUpdate();

        if (state === undefined) {
            return;
        }

        this.updateState(state);
    }

    private handleExitGame(): void {
        removePlayerSessionId();
        window.location.reload();
    }

    private updateBackgroundMusicVolume(newVolume: number): void {
        this.backgroundMusicVolume = newVolume;
        const musicPlayer: HTMLAudioElement | null = this.shadowRoot?.getElementById(
            "roomMusic"
        ) as HTMLAudioElement;
        if (musicPlayer) {
            musicPlayer.volume = newVolume;
        }
    }

    private updateEffectsVolume(newVolume: number): void {
        this.effectsVolume = newVolume;
        console.log(newVolume);
        // Implement effect volume adjustment logic here
    }

    private handleBackgroundMusicVolumeChange(e: Event): void {
        this.updateBackgroundMusicVolume((e.target as HTMLInputElement).valueAsNumber);
    }

    private handleEffectsVolumeChange(e: Event): void {
        this.updateEffectsVolume((e.target as HTMLInputElement).valueAsNumber);
    }

    protected render(): TemplateResult {
        return html`
            ${this.showPopup
                ? html`
                      <div
                          @click="${this.handlePopupClick}"
                          style="position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0, 0, 0, 0.1); display: flex; justify-content: center; align-items: center; z-index: 1000;"
                      ></div>
                  `
                : nothing}
            <div class="game">
                ${this.renderTitle()} ${this.renderHeader()} ${this.renderContent()} ${this.renderFooter()}
            </div>
            <audio id="roomMusic" loop></audio>
        `;
    }

    private renderTitle(): TemplateResult {
        if (this.roomTitle) {
            return html`<div class="title">${this.roomTitle}</div>`;
        }

        return html`${nothing}`;
    }

    private renderHeader(): TemplateResult {
        if (this.roomImages && this.roomImages.length > 0) {
            return html`
                <div class="header">
                    ${this.roomImages?.map((url) => html`<img src="/assets/img/rooms/${url}.png" />`)}
                </div>
            `;
        }

        return html`${nothing}`;
    }

    private renderContent(): TemplateResult {
        return html`<div class="content">${this.contentText?.map((text) => html`<p>${text}</p>`)}</div>`;
    }

    private renderFooter(): TemplateResult {
        return html`
            <div class="footer">
                <div class="volume-controls">
                    <label>Background Music Volume:</label>
                    <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.01"
                        .value="${this.backgroundMusicVolume.toString()}"
                        @input="${this.handleBackgroundMusicVolumeChange}"
                    />
                    <label>Effects Volume:</label>
                    <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.01"
                        .value="${this.effectsVolume.toString()}"
                        @input="${this.handleEffectsVolumeChange}"
                    />
                </div>
                <div class="buttons">
                    <div>
                        ${this.actionButtons?.map(
                            (button) => html`<a
                                class="button ${this.selectedActionButton === button ? "active" : ""}"
                                @click=${(): void => void this.handleClickAction(button)}
                                >${button.label}</a
                            >`
                        )}
                        ${this.roomTitle !== "Tanner de Virusscanner"
                            ? html`<a class="button" @click=${this.handleExitGame}>Exit game</a>`
                            : ""}
                    </div>
                    <div>
                        ${this.selectedActionButton
                            ? this.gameObjectButtons?.map(
                                  (button) => html`<a
                                      class="button ${this.selectedGameObjectButtons.has(button)
                                          ? "active"
                                          : ""}"
                                      @click=${(): void => void this.handleClickObject(button)}
                                      >${button.name}</a
                                  >`
                              )
                            : nothing}
                    </div>
                </div>
            </div>
        `;
    }
}
